if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      static get CONFIG() {
        return {
          ACTIVE_CLASS: "active",
          MAX_RETRIES: 20,
          CHECK_INTERVAL: 200,
          SLIDE_TRANSITION_DELAY: 100,
          INTERSECTION_THRESHOLD: 0.1,
          SELECTORS: {
            SWIPER_CONTAINER: ".wt-slider__container",
            SOUND_TOGGLE: ".wt-video__sound-toggle",
            VOLUME_ICON: ".svg-icon--volume",
            MUTED_ICON: ".svg-icon--volume-muted",
            VIDEO: "video",
          },
        };
      }

      constructor() {
        super();
        this.state = {
          globalSoundEnabled: false,
          retryCount: 0,
          isInitialized: false,
        };

        this.references = {
          swiper: null,
          swiperInstance: null,
          checkInterval: null,
          sectionObserver: null,
          eventListeners: new Map(),
          videoHandlersInitialized: false,
        };

        this.boundMethods = {
          handleSwiperReady: this.handleSwiperReady.bind(this),
          handleSlideChange: this.handleSlideChange.bind(this),
          handleIntersectionChange: this.handleIntersectionChange.bind(this),
        };
      }

      connectedCallback() {
        if (this.state.isInitialized) {
          return;
        }

        this.addEventListener("swiperReady", this.boundMethods.handleSwiperReady);
        this.init();
        this.state.isInitialized = true;
      }

      disconnectedCallback() {
        this.cleanup();
      }

      async init() {
        try {
          this.state.retryCount = 0;
          this.references.swiper = this.querySelector(VideoReels.CONFIG.SELECTORS.SWIPER_CONTAINER);

          if (!this.references.swiper) {
            this.logError("Swiper container not found during initialization");
            return;
          }

          await this.waitForSwiperInitialization();
        } catch (error) {
          this.logError("Failed to initialize VideoReels component", error);
        }
      }

      async waitForSwiperInitialization() {
        return new Promise((resolve, reject) => {
          const checkInitialization = () => {
            this.state.retryCount++;

            if (this.state.retryCount > VideoReels.CONFIG.MAX_RETRIES) {
              this.logError("Max retries reached for Swiper initialization");
              this.clearCheckInterval();
              reject(new Error("Swiper initialization timeout"));
              return;
            }

            if (this.isSwiperReady()) {
              this.clearCheckInterval();
              this.initializeVideoHandlers();
              resolve();
            }
          };

          if (this.isSwiperReady()) {
            this.initializeVideoHandlers();
            resolve();
            return;
          }

          this.references.checkInterval = setInterval(checkInitialization, VideoReels.CONFIG.CHECK_INTERVAL);
        });
      }

      isSwiperReady() {
        const { swiper } = this.references;
        const isReady = swiper && swiper.classList.contains("swiper-initialized") && swiper.swiper;
        return isReady;
      }

      handleSwiperReady(event) {
        if (!event || !event.detail) {
          return;
        }

        const swiperInstance = event.detail?.swiper;
        if (!swiperInstance) {
          return;
        }

        this.references.swiperInstance = swiperInstance;
        this.initializeVideoHandlers();
      }

      initializeVideoHandlers() {
        if (this.references.videoHandlersInitialized) {
          return;
        }

        const swiperInstance = this.references.swiperInstance || this.references.swiper?.swiper;
        if (!swiperInstance) {
          this.logError("Swiper instance not available for video handlers");
          return;
        }

        this.references.swiperInstance = swiperInstance;
        try {
          if (typeof swiperInstance.off === "function") {
            swiperInstance.off("slideChange", this.boundMethods.handleSlideChange);
          }

          this.setupSlideChangeHandler(swiperInstance);
          this.setupSoundToggleHandlers(swiperInstance);
          this.setupIntersectionObserver();

          this.handleSlideChange(swiperInstance);
          this.clearCheckInterval();

          this.references.videoHandlersInitialized = true;
        } catch (error) {
          this.logError("Failed to initialize video handlers", error);
        }
      }

      setupSlideChangeHandler(swiperInstance) {
        swiperInstance.on("slideChange", this.boundMethods.handleSlideChange);
      }

      setupSoundToggleHandlers(swiperInstance) {
        swiperInstance.slides.forEach((slide, index) => {
          const button = slide.querySelector(VideoReels.CONFIG.SELECTORS.SOUND_TOGGLE);
          if (!button) {
            return;
          }

          const video = slide.querySelector(VideoReels.CONFIG.SELECTORS.VIDEO);
          if (!video) {
            return;
          }

          this.setupSoundToggleForSlide(button, video, index);
        });
      }

      setupSoundToggleForSlide(button, video, slideIndex) {
        this.removeManagedListener(button);

        const soundToggleHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();

          try {
            this.toggleVideoSound(video, button);
          } catch (error) {
            this.logError(`Failed to toggle sound for slide ${slideIndex}`, error);
          }
        };

        const key = `sound-toggle-${slideIndex}`;
        this.addManagedListener(button, "click", soundToggleHandler, key);
      }

      toggleVideoSound(video, button) {
        const wasMuted = video.muted;
        video.muted = !wasMuted;
        this.state.globalSoundEnabled = wasMuted;
        this.updateSoundButtonVisual(button, wasMuted);
      }

      updateSoundButtonVisual(button, soundEnabled) {
        if (!button) {
          return;
        }

        const volumeIcon = button.querySelector(VideoReels.CONFIG.SELECTORS.VOLUME_ICON);
        const mutedIcon = button.querySelector(VideoReels.CONFIG.SELECTORS.MUTED_ICON);

        if (volumeIcon) {
          volumeIcon.style.display = soundEnabled ? "block" : "none";
        }

        if (mutedIcon) {
          mutedIcon.style.display = soundEnabled ? "none" : "block";
        }

        button.setAttribute("aria-pressed", soundEnabled.toString());
        button.setAttribute("aria-label", soundEnabled ? "Mute video" : "Unmute video");
      }

      setupIntersectionObserver() {
        if (this.references.sectionObserver) {
          return;
        }

        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: VideoReels.CONFIG.INTERSECTION_THRESHOLD,
        };

        this.references.sectionObserver = new IntersectionObserver(
          this.boundMethods.handleIntersectionChange,
          observerOptions,
        );

        this.references.sectionObserver.observe(this);
      }

      handleIntersectionChange(entries) {
        entries.forEach((entry) => {
          const activeSlide = this.getActiveSlide();
          if (!activeSlide) {
            return;
          }

          const video = activeSlide.querySelector(VideoReels.CONFIG.SELECTORS.VIDEO);
          if (!video) {
            return;
          }

          if (entry.isIntersecting) {
            this.playVideoInActiveSlide(this.references.swiperInstance);
          } else {
            this.pauseVideo(video);
          }
        });
      }

      handleSlideChange(swiperInstance) {
        try {
          this.pauseAllVideos();
          this.updateAllSoundButtonVisuals(swiperInstance);
          this.updateActiveSlideClass(swiperInstance);

          requestAnimationFrame(() => {
            setTimeout(() => {
              this.playVideoInActiveSlide(swiperInstance);
            }, VideoReels.CONFIG.SLIDE_TRANSITION_DELAY);
          });
        } catch (error) {
          this.logError("Failed to handle slide change", error);
        }
      }

      getActiveSlide(swiperInstance = this.references.swiperInstance) {
        if (!swiperInstance) {
          return this.querySelector(".swiper-slide-active");
        }

        return swiperInstance.slides[swiperInstance.activeIndex];
      }

      updateActiveSlideClass(swiperInstance) {
        const activeSlide = this.getActiveSlide(swiperInstance);
        if (!activeSlide) {
          return;
        }

        swiperInstance.slides.forEach((slide) => {
          slide.classList.toggle(VideoReels.CONFIG.ACTIVE_CLASS, slide === activeSlide);
        });
      }

      playVideoInActiveSlide(swiperInstance) {
        const activeSlide = this.getActiveSlide(swiperInstance);
        if (!activeSlide) {
          return;
        }

        const video = activeSlide.querySelector(VideoReels.CONFIG.SELECTORS.VIDEO);
        if (!video) {
          return;
        }

        video.muted = !this.state.globalSoundEnabled;
        this.playVideo(video);

        const button = activeSlide.querySelector(VideoReels.CONFIG.SELECTORS.SOUND_TOGGLE);
        if (!button) {
          return;
        }

        this.updateSoundButtonVisual(button, this.state.globalSoundEnabled);
      }

      async playVideo(video) {
        try {
          await video.play();
        } catch (error) {
          this.logError("Video play failed", error);
        }
      }

      pauseVideo(video) {
        try {
          video.pause();
        } catch (error) {
          this.logError("Video pause failed", error);
        }
      }

      pauseAllVideos() {
        const videos = this.querySelectorAll(VideoReels.CONFIG.SELECTORS.VIDEO);
        videos.forEach((video) => {
          this.pauseVideo(video);
          video.muted = true;
        });
      }

      updateAllSoundButtonVisuals(swiperInstance) {
        swiperInstance.slides.forEach((slide) => {
          const button = slide.querySelector(VideoReels.CONFIG.SELECTORS.SOUND_TOGGLE);
          const video = slide.querySelector(VideoReels.CONFIG.SELECTORS.VIDEO);

          if (button && video) {
            this.updateSoundButtonVisual(button, !video.muted);
          }
        });
      }

      addManagedListener(element, eventType, handler, key) {
        if (!element || !eventType || typeof handler !== "function") return null;

        const listenerKey = key || `${eventType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        this.references.eventListeners.set(listenerKey, {
          element,
          eventType,
          handler,
        });

        try {
          if (element instanceof HTMLElement) {
            element.dataset.vrListenerKey = listenerKey;
          }
        } catch (e) {}

        element.addEventListener(eventType, handler);
        return listenerKey;
      }

      removeManagedListener(element, key) {
        let listenerKey = key;
        if (!listenerKey && element && element.dataset?.vrListenerKey) {
          listenerKey = element.dataset.vrListenerKey;
        }

        if (!listenerKey) {
          return;
        }

        const listener = this.references.eventListeners.get(listenerKey);
        if (listener) {
          try {
            listener.element.removeEventListener(listener.eventType, listener.handler);
          } catch (e) {}
          this.references.eventListeners.delete(listenerKey);
          try {
            if (listener.element instanceof HTMLElement && listener.element.dataset.vrListenerKey) {
              delete listener.element.dataset.vrListenerKey;
            }
          } catch (e) {}
        }
      }

      removeManagedListenerByKey(key) {
        if (!key) {
          return;
        }
        const listener = this.references.eventListeners.get(key);
        if (!listener) {
          return;
        }

        try {
          listener.element.removeEventListener(listener.eventType, listener.handler);
        } catch (e) {}
        this.references.eventListeners.delete(key);
        try {
          if (listener.element instanceof HTMLElement && listener.element.dataset.vrListenerKey === key) {
            delete listener.element.dataset.vrListenerKey;
          }
        } catch (e) {}
      }

      clearCheckInterval() {
        if (this.references.checkInterval) {
          clearInterval(this.references.checkInterval);
          this.references.checkInterval = null;
        }
      }

      logError(message, error = null) {
        const fullMessage = `VideoReels: ${message}`;
        if (error) {
          console.error(fullMessage, error);
        } else {
          console.warn(fullMessage);
        }
      }

      cleanup() {
        this.clearCheckInterval();

        if (this.references.sectionObserver) {
          this.references.sectionObserver.disconnect();
          this.references.sectionObserver = null;
        }

        this.references.eventListeners.forEach((listener, key) => {
          try {
            listener.element.removeEventListener(listener.eventType, listener.handler);
          } catch (e) {}
        });
        this.references.eventListeners.clear();

        if (this.references.swiperInstance && typeof this.references.swiperInstance.off === "function") {
          this.references.swiperInstance.off("slideChange", this.boundMethods.handleSlideChange);
        }

        try {
          this.removeEventListener("swiperReady", this.boundMethods.handleSwiperReady);
        } catch (e) {}

        this.pauseAllVideos();

        this.references.videoHandlersInitialized = false;
        this.state.isInitialized = false;
      }
    },
  );
}
